import { rpc } from './rpc'

/**
 * Everything the sickness screens read and write.
 *
 * All of it goes through database functions rather than table reads, and that
 * is the design rather than a convenience. front_desk holds no select on any
 * sickness table: it cannot read an episode, a reason for absence or a fit
 * note, because app.member_org_ids() excludes the role entirely (0026). The
 * column lists inside those functions ARE the access control, so a screen
 * cannot widen what reception sees by asking for more.
 */

export interface FrontDeskPerson {
  personId: string
  fullName: string
  department: string | null
  organisationCode: string
}

/** Name and department. Deliberately nothing else. */
export async function searchPeople(search: string): Promise<FrontDeskPerson[]> {
  const rows = await rpc<
    { person_id: string; full_name: string; department: string | null; organisation_code: string }[]
  >('front_desk_people', { p_search: search })
  return (rows ?? []).map((r) => ({
    personId: r.person_id,
    fullName: r.full_name,
    department: r.department,
    organisationCode: r.organisation_code,
  }))
}

export type ShiftWriteStatus =
  | 'not_applicable'
  | 'pending'
  | 'written'
  | 'failed'
  | 'reverted'

export interface SicknessLogged {
  alreadyLogged: boolean
  episodeId: string
  fullName: string
  department: string | null
  absenceDate: string
  lastAbsentDate: string | null
  expectedReturnDate: string | null
  daysCreated: number
  daysProjected: number
  /** Days in the range already recorded on another live absence. */
  daysSkipped: number
  shiftWriteStatus: ShiftWriteStatus
}

/**
 * Records the absence.
 *
 * An expected return date is optional, because at six in the morning somebody
 * often does not know. Passing one records every day up to the day before it,
 * and the days after today come back counted as `daysProjected`: the screen
 * says so, because those are days nobody has yet been absent on.
 */
export async function reportSickness(
  personId: string,
  firstAbsentDate: string,
  expectedReturnDate: string | null,
): Promise<SicknessLogged> {
  const r = await rpc<{
    already_logged: boolean
    episode_id: string
    full_name: string
    department: string | null
    absence_date: string
    last_absent_date?: string | null
    expected_return_date?: string | null
    days_created?: number
    days_projected?: number
    days_skipped?: number
    shift_write_status: ShiftWriteStatus
  }>('report_sickness_period', {
    p_person_id: personId,
    p_first_absent_date: firstAbsentDate,
    p_expected_return_date: expectedReturnDate,
  })

  return {
    alreadyLogged: r.already_logged,
    episodeId: r.episode_id,
    fullName: r.full_name,
    department: r.department,
    absenceDate: r.absence_date,
    lastAbsentDate: r.last_absent_date ?? null,
    expectedReturnDate: r.expected_return_date ?? null,
    daysCreated: r.days_created ?? 1,
    daysProjected: r.days_projected ?? 0,
    daysSkipped: r.days_skipped ?? 0,
    shiftWriteStatus: r.shift_write_status,
  }
}

export interface MyLog {
  dayId: string
  fullName: string
  department: string | null
  absenceDate: string
  loggedAt: string
  shiftWriteStatus: ShiftWriteStatus
}

/** What this user logged recently, so a wrong name is caught in minutes. */
export async function myRecentLogs(): Promise<MyLog[]> {
  const rows = await rpc<
    {
      day_id: string
      full_name: string
      department: string | null
      absence_date: string
      logged_at: string
      shift_write_status: ShiftWriteStatus
    }[]
  >('my_sickness_logs')
  return (rows ?? []).map((r) => ({
    dayId: r.day_id,
    fullName: r.full_name,
    department: r.department,
    absenceDate: r.absence_date,
    loggedAt: r.logged_at,
    shiftWriteStatus: r.shift_write_status,
  }))
}

/* ── the HR board ─────────────────────────────────────────
   Reads here are ordinary table reads, so RLS decides what comes back: HR sees
   the hotel, a head of department sees their own departments, and front desk
   sees nothing at all. Every list filters out cancelled absences, because a
   cancelled one is a record of a mistake and not of somebody being ill. */

import { supabase } from '@/lib/supabase'

export type EpisodeStatus = 'open' | 'closed' | 'cancelled'
export type PayTreatment = 'ssp' | 'company_paid' | 'company_unpaid' | 'unpaid_other'
export type CertKind = 'self_certification' | 'fit_note'
export type CertStatus = 'awaited' | 'received' | 'overdue' | 'not_required' | 'waived'

function fail(context: string, error: { message: string } | null): never {
  throw new Error(`${context}: ${error?.message ?? 'unknown error'}`)
}

export interface EpisodeRow {
  episodeId: string
  teamMember: string
  jobTitle: string | null
  department: string | null
  firstAbsentDate: string
  lastAbsentDate: string | null
  expectedReturnDate: string | null
  status: EpisodeStatus
  daysRecorded: number
  workingDaysAbsent: number
  daysNotYetClassified: number
  daysAbsentLast6Months: number
  absencesLast6Months: number
  interviewId: string | null
  interviewAt: string | null
  completedAt: string | null
}

const EPISODE_COLS =
  'episode_id, team_member, job_title, department, first_absent_date, last_absent_date, expected_return_date, status, days_recorded, working_days_absent, days_not_yet_classified, days_absent_last_6_months, absences_last_6_months, interview_id, interview_at, completed_at'

function toEpisode(r: Record<string, unknown>): EpisodeRow {
  return {
    episodeId: r.episode_id as string,
    teamMember: r.team_member as string,
    jobTitle: (r.job_title as string | null) ?? null,
    department: (r.department as string | null) ?? null,
    firstAbsentDate: r.first_absent_date as string,
    lastAbsentDate: (r.last_absent_date as string | null) ?? null,
    expectedReturnDate: (r.expected_return_date as string | null) ?? null,
    status: r.status as EpisodeStatus,
    daysRecorded: Number(r.days_recorded ?? 0),
    workingDaysAbsent: Number(r.working_days_absent ?? 0),
    daysNotYetClassified: Number(r.days_not_yet_classified ?? 0),
    daysAbsentLast6Months: Number(r.days_absent_last_6_months ?? 0),
    absencesLast6Months: Number(r.absences_last_6_months ?? 0),
    interviewId: (r.interview_id as string | null) ?? null,
    interviewAt: (r.interview_at as string | null) ?? null,
    completedAt: (r.completed_at as string | null) ?? null,
  }
}

export interface OutstandingCert {
  id: string
  episodeId: string
  teamMember: string
  department: string | null
  kind: CertKind
  status: CertStatus
  dueOn: string | null
  coversFrom: string | null
}

export interface UnreviewedPay {
  episodeId: string
  teamMember: string
  department: string | null
  days: number
  firstAbsentDate: string
  treatment: PayTreatment
}

/** A day whose Planday shift has not been dealt with yet, or would not go. */
export interface RotaWaiting {
  teamMember: string
  department: string | null
  absenceDate: string
  status: ShiftWriteStatus
  revertRequested: boolean
  error: string | null
}

export interface SicknessBoard {
  open: EpisodeRow[]
  awaitingInterview: EpisodeRow[]
  certifications: OutstandingCert[]
  unreviewedPay: UnreviewedPay[]
  offToday: number
  /**
   * Days queued for Planday, or that failed.
   *
   * On the board because nothing else told anybody. The screen records the
   * decision and a worker performs the write, so if the worker is not running
   * the rota silently never changes and every screen still looks correct.
   */
  rotaWaiting: RotaWaiting[]
}

export async function loadSicknessBoard(organisationId: string): Promise<SicknessBoard> {
  const today = new Date().toISOString().slice(0, 10)

  const open = await supabase
    .from('return_to_work_pack')
    .select(EPISODE_COLS)
    .eq('organisation_id', organisationId)
    .eq('status', 'open')
    .order('first_absent_date', { ascending: true })
  if (open.error) fail('Could not load open absences', open.error)

  // Closed, with no interview completed. This is the list that actually gets
  // forgotten: the person is back at work, so nothing prompts anybody.
  const awaiting = await supabase
    .from('return_to_work_pack')
    .select(EPISODE_COLS)
    .eq('organisation_id', organisationId)
    .eq('status', 'closed')
    .is('completed_at', null)
    .order('last_absent_date', { ascending: true })
    .limit(50)
  if (awaiting.error) fail('Could not load returns awaiting an interview', awaiting.error)

  const certs = await supabase
    .from('sickness_certifications')
    .select('id, episode_id, kind, status, due_on, covers_from, sickness_episodes!inner(id, status)')
    .eq('organisation_id', organisationId)
    .in('status', ['awaited', 'overdue'])
    .order('due_on', { ascending: true })
    .limit(100)
  if (certs.error) fail('Could not load outstanding paperwork', certs.error)

  const days = await supabase
    .from('sickness_days')
    .select('episode_id, absence_date, pay_treatment, sickness_episodes!inner(status)')
    .eq('organisation_id', organisationId)
    .eq('pay_treatment_confirmed', false)
    .neq('sickness_episodes.status', 'cancelled')
    .order('absence_date', { ascending: false })
    .limit(1000)
  if (days.error) fail('Could not load the pay review queue', days.error)

  // Cancelled absences must not be counted. The tile read "2 off today" while
  // one person was off, because a day belonging to an absence somebody had
  // already marked as logged in error was still being counted.
  const waiting = await supabase
    .from('sickness_days')
    .select('absence_date, shift_write_status, shift_revert_requested, shift_write_error, employment_id, sickness_episodes!inner(status)')
    .eq('organisation_id', organisationId)
    .neq('sickness_episodes.status', 'cancelled')
    .or('shift_write_status.eq.pending,shift_write_status.eq.failed,shift_revert_requested.eq.true')
    .order('absence_date', { ascending: true })
    .limit(200)
  if (waiting.error) fail('Could not load the Planday queue', waiting.error)

  const offNow = await supabase
    .from('sickness_days')
    .select('id, sickness_episodes!inner(status)', { count: 'exact', head: true })
    .eq('organisation_id', organisationId)
    .eq('absence_date', today)
    .neq('sickness_episodes.status', 'cancelled')
  if (offNow.error) fail('Could not count who is off today', offNow.error)

  // Names for the two lists keyed by episode rather than by person.
  const openRows = (open.data ?? []).map((r) => toEpisode(r as Record<string, unknown>))
  const awaitingRows = (awaiting.data ?? []).map((r) => toEpisode(r as Record<string, unknown>))
  const nameByEpisode = new Map<string, { name: string; department: string | null }>()
  for (const e of [...openRows, ...awaitingRows]) {
    nameByEpisode.set(e.episodeId, { name: e.teamMember, department: e.department })
  }

  const certifications: OutstandingCert[] = (certs.data ?? [])
    .filter((c) => {
      const ep = (c as Record<string, unknown>).sickness_episodes as { status?: string } | null
      return ep?.status !== 'cancelled'
    })
    .map((c) => {
      const r = c as Record<string, unknown>
      const who = nameByEpisode.get(r.episode_id as string)
      return {
        id: r.id as string,
        episodeId: r.episode_id as string,
        teamMember: who?.name ?? 'Another absence',
        department: who?.department ?? null,
        kind: r.kind as CertKind,
        status: r.status as CertStatus,
        dueOn: (r.due_on as string | null) ?? null,
        coversFrom: (r.covers_from as string | null) ?? null,
      }
    })

  const byEpisode = new Map<string, UnreviewedPay>()
  for (const d of days.data ?? []) {
    const r = d as Record<string, unknown>
    const id = r.episode_id as string
    const who = nameByEpisode.get(id)
    const existing = byEpisode.get(id)
    if (existing === undefined) {
      byEpisode.set(id, {
        episodeId: id,
        teamMember: who?.name ?? 'A closed absence',
        department: who?.department ?? null,
        days: 1,
        firstAbsentDate: r.absence_date as string,
        treatment: r.pay_treatment as PayTreatment,
      })
    } else {
      existing.days += 1
      if ((r.absence_date as string) < existing.firstAbsentDate) {
        existing.firstAbsentDate = r.absence_date as string
      }
    }
  }

  // Names come from the two episode lists, which are the only place this
  // function reads them. A waiting day on a closed absence shows without one
  // rather than being dropped: the rota still needs fixing either way.
  const nameByEmployment = new Map<string, { name: string; department: string | null }>()
  for (const r of [...(open.data ?? []), ...(awaiting.data ?? [])]) {
    const row = r as Record<string, unknown>
    nameByEmployment.set(String(row.episode_id), {
      name: row.team_member as string,
      department: (row.department as string | null) ?? null,
    })
  }

  const rotaWaiting: RotaWaiting[] = (waiting.data ?? []).map((w) => {
    const row = w as Record<string, unknown>
    return {
      teamMember: 'A recorded absence',
      department: null,
      absenceDate: row.absence_date as string,
      status: row.shift_write_status as ShiftWriteStatus,
      revertRequested: row.shift_revert_requested as boolean,
      error: (row.shift_write_error as string | null) ?? null,
    }
  })

  return {
    open: openRows,
    awaitingInterview: awaitingRows,
    certifications,
    unreviewedPay: [...byEpisode.values()].sort((a, b) => b.days - a.days),
    offToday: offNow.count ?? 0,
    rotaWaiting,
  }
}

/** Closes an absence on the real return date, from the return to work form. */
export async function confirmReturn(
  episodeId: string,
  actualReturnDate: string,
): Promise<{ daysAbsent: number; projectedRemoved: number; revertsQueued: number }> {
  const r = await rpc<{
    days_absent: number
    projected_days_removed: number
    shift_reverts_queued: number
  }>('confirm_return', { p_episode_id: episodeId, p_actual_return_date: actualReturnDate })
  return {
    daysAbsent: r.days_absent,
    projectedRemoved: r.projected_days_removed,
    revertsQueued: r.shift_reverts_queued,
  }
}

/** For an absence that should never have been recorded. A reason is required. */
export async function cancelSickness(episodeId: string, reason: string): Promise<void> {
  await rpc('cancel_sickness', { p_episode_id: episodeId, p_reason: reason })
}

/**
 * Confirms how an absence is paid.
 *
 * pay_treatment_set_by is stamped by the database, not sent from here, because
 * a screen can forget and a screen can lie, and that stamp is the evidence
 * somebody actually looked.
 */
export async function confirmPay(
  episodeId: string,
  treatment: PayTreatment,
  note: string | null,
): Promise<number> {
  const { data, error } = await supabase
    .from('sickness_days')
    .update({
      pay_treatment: treatment,
      pay_treatment_confirmed: true,
      pay_treatment_note: note,
    } as never)
    .eq('episode_id', episodeId)
    .eq('pay_treatment_confirmed', false)
    .select('id')
  if (error) fail('Could not confirm the pay treatment', error)
  return (data ?? []).length
}

/** Marks a self-certification or fit note as received. */
export async function markCertificationReceived(id: string, receivedOn: string): Promise<void> {
  const { error } = await supabase
    .from('sickness_certifications')
    .update({ status: 'received', received_on: receivedOn } as never)
    .eq('id', id)
  if (error) fail('Could not record the paperwork', error)
}

/* ── the return to work interview ─────────────────────────
   Six of the paper form's fields are not here because the system already knows
   them: name, job title, department, first day, return date, and both day
   counts. They come from return_to_work_pack. Re-typing a date is how two
   records end up disagreeing and nobody knows which is right. */

export type RtwAnswer = 'yes' | 'no' | 'not_applicable' | 'unknown'
export type RtwAckMethod = 'in_app' | 'emailed_copy' | 'printed_copy_given' | 'declined'

export interface RtwInterview {
  id: string
  episodeId: string
  conductedByName: string | null
  interviewAt: string | null
  reasonForAbsence: string | null
  visitedDoctorOrHospital: RtwAnswer
  visitDetail: string | null
  prescribedMedication: RtwAnswer
  feelingNow: string | null
  followedReportingProcedure: RtwAnswer
  sicknessDocsReviewed: RtwAnswer
  otherComments: string | null
  completedAt: string | null
  ackMethod: RtwAckMethod | null
  ackAt: string | null
}

const RTW_COLS =
  'id, episode_id, conducted_by_name, interview_at, reason_for_absence, visited_doctor_or_hospital, visit_detail, prescribed_medication, feeling_now, followed_reporting_procedure, sickness_docs_reviewed, other_comments, completed_at, team_member_ack_method, team_member_ack_at'

function toInterview(r: Record<string, unknown>): RtwInterview {
  return {
    id: r.id as string,
    episodeId: r.episode_id as string,
    conductedByName: (r.conducted_by_name as string | null) ?? null,
    interviewAt: (r.interview_at as string | null) ?? null,
    reasonForAbsence: (r.reason_for_absence as string | null) ?? null,
    visitedDoctorOrHospital: (r.visited_doctor_or_hospital as RtwAnswer) ?? 'unknown',
    visitDetail: (r.visit_detail as string | null) ?? null,
    prescribedMedication: (r.prescribed_medication as RtwAnswer) ?? 'unknown',
    feelingNow: (r.feeling_now as string | null) ?? null,
    followedReportingProcedure: (r.followed_reporting_procedure as RtwAnswer) ?? 'unknown',
    sicknessDocsReviewed: (r.sickness_docs_reviewed as RtwAnswer) ?? 'unknown',
    otherComments: (r.other_comments as string | null) ?? null,
    completedAt: (r.completed_at as string | null) ?? null,
    ackMethod: (r.team_member_ack_method as RtwAckMethod | null) ?? null,
    ackAt: (r.team_member_ack_at as string | null) ?? null,
  }
}

export async function loadEpisode(
  episodeId: string,
): Promise<{ episode: EpisodeRow; interview: RtwInterview | null }> {
  const pack = await supabase
    .from('return_to_work_pack')
    .select(EPISODE_COLS)
    .eq('episode_id', episodeId)
    .maybeSingle()
  if (pack.error) fail('Could not load the absence', pack.error)
  if (pack.data === null) throw new Error('That absence is not visible to your account.')

  const rtw = await supabase
    .from('return_to_work_interviews')
    .select(RTW_COLS)
    .eq('episode_id', episodeId)
    .maybeSingle()
  if (rtw.error) fail('Could not load the interview', rtw.error)

  return {
    episode: toEpisode(pack.data as Record<string, unknown>),
    interview: rtw.data === null ? null : toInterview(rtw.data as Record<string, unknown>),
  }
}

export interface RtwDraft {
  conductedByName: string
  interviewAt: string
  reasonForAbsence: string
  visitedDoctorOrHospital: RtwAnswer
  visitDetail: string
  prescribedMedication: RtwAnswer
  feelingNow: string
  followedReportingProcedure: RtwAnswer
  sicknessDocsReviewed: RtwAnswer
  otherComments: string
}

/**
 * Saves the interview, and only marks it complete when asked.
 *
 * A draft is a real state: these conversations get interrupted by a fire alarm
 * or a delivery, and losing what was said because the manager could not finish
 * in one sitting is how people go back to paper.
 */
export async function saveInterview(
  episodeId: string,
  organisationId: string,
  draft: RtwDraft,
  complete: boolean,
): Promise<void> {
  const row = {
    episode_id: episodeId,
    organisation_id: organisationId,
    conducted_by_name: draft.conductedByName.trim() === '' ? null : draft.conductedByName.trim(),
    interview_at: draft.interviewAt === '' ? null : draft.interviewAt,
    reason_for_absence: draft.reasonForAbsence.trim() === '' ? null : draft.reasonForAbsence.trim(),
    visited_doctor_or_hospital: draft.visitedDoctorOrHospital,
    visit_detail: draft.visitDetail.trim() === '' ? null : draft.visitDetail.trim(),
    prescribed_medication: draft.prescribedMedication,
    feeling_now: draft.feelingNow.trim() === '' ? null : draft.feelingNow.trim(),
    followed_reporting_procedure: draft.followedReportingProcedure,
    sickness_docs_reviewed: draft.sicknessDocsReviewed,
    other_comments: draft.otherComments.trim() === '' ? null : draft.otherComments.trim(),
    ...(complete ? { completed_at: new Date().toISOString() } : {}),
  }

  const { error } = await supabase
    .from('return_to_work_interviews')
    .upsert(row as never, { onConflict: 'episode_id' })
  if (error) fail('Could not save the interview', error)
}

/** The copy the procedure requires the team member to get. Replaces a signature. */
export async function recordAcknowledgement(
  episodeId: string,
  method: RtwAckMethod,
): Promise<void> {
  const { error } = await supabase
    .from('return_to_work_interviews')
    .update({
      team_member_ack_method: method,
      team_member_ack_at: new Date().toISOString(),
    } as never)
    .eq('episode_id', episodeId)
  if (error) fail('Could not record that they got a copy', error)
}
